import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import colors from 'constants/colors';
import Button from 'components/Button';
import Textarea from 'react-textarea-autosize';

const selectable = css`
  background: ${colors.main.white};
  border: 1.5px solid ${colors.main.black};
  color: ${colors.main.black};
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition:
    background 0.15s,
    color 0.15s;

  &[aria-pressed='true'] {
    background: ${colors.main.black};
    color: ${colors.main.white};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }

  @media (hover: hover) {
    &:not([aria-pressed='true']):not(:disabled):hover {
      background: ${colors.main.secondaryBtns};
    }
  }
`;

export type Tone = 'light' | 'dark';

/** Dark matches the player's between-phase overlays (the Prep assessment). */
export const Question = styled.div<{ $tone?: Tone }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;

  ${({ $tone }) =>
    $tone === 'dark' &&
    css`
      align-items: center;
      gap: 16px;
      margin-bottom: 32px;
      text-align: center;
    `}
`;

export const ContextChip = styled.span`
  align-self: flex-start;
  background: ${colors.main.black};
  border-radius: 999px;
  color: ${colors.main.white};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  padding: 6px 12px;
  text-transform: uppercase;
`;

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const darkCard = css`
  background: ${rgba(colors.main.dividers, 0.9)};
  border-color: transparent;
  border-radius: 4px;
  transition: all 0.3s;

  &[aria-pressed='true'] {
    background: ${rgba(colors.main.blue, 0.9)};
  }

  @media (hover: hover) {
    &:not([aria-pressed='true']):not(:disabled):hover {
      background: ${rgba(colors.main.blue, 0.9)};
      color: ${colors.main.white};
    }
  }
`;

export const RatingCard = styled.button<{ $tone?: Tone }>`
  ${selectable}
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 118px;
  padding: 16px 14px 14px;

  ${({ $tone }) => $tone === 'dark' && darkCard}
`;

export const RatingLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const RatingDescription = styled.span`
  font-size: 13px;
  line-height: 1.4;
  opacity: 0.8;
`;

export const ExerciseOption = styled.button`
  ${selectable}
  align-items: center;
  border-radius: 12px;
  display: flex;
  gap: 10px;
  padding: 10px 12px;
`;

export const ExerciseThumb = styled.span<{ image?: string }>`
  background: ${({ image }) => (image ? `url('${image}') center / cover` : colors.main.dividers)};
  border-radius: 8px;
  flex-shrink: 0;
  height: 44px;
  width: 44px;
`;

export const ExerciseName = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
`;

export const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
`;

export const TagOption = styled.button`
  ${selectable}
  border-radius: 999px;
  font-size: 15px;
  padding: 9px 16px;

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;

export const StepperSegments = styled.div`
  display: flex;
  gap: 5px;
`;

export const StepperSegment = styled.span<{ state: 'done' | 'current' | 'todo' }>`
  background: ${({ state }) =>
    ({ done: colors.main.green, current: colors.main.blue, todo: colors.main.disabledBtns })[
      state
    ]};
  border-radius: 3px;
  flex: 1;
  height: 5px;
`;

export const StepperMeta = styled.div`
  display: flex;
  font-size: 12px;
  font-weight: 700;
  justify-content: space-between;
  letter-spacing: 0.12em;
  margin: 8px 0 24px;
  text-transform: uppercase;

  span:first-child {
    color: ${colors.main.blue};
  }

  span:last-child {
    color: ${colors.main.grey};
  }
`;

export const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 28px;

  @media screen and (max-width: 992px) {
    background: ${colors.main.white};
    bottom: 0;
    box-shadow: 0 -8px 24px rgba(33, 38, 45, 0.08);
    left: 0;
    margin: 0;
    padding: 12px 16px;
    position: fixed;
    right: 0;
    z-index: 1000;
  }
`;

export const PrimaryAction = styled(Button)`
  width: 260px;

  @media screen and (max-width: 992px) {
    flex: 1;
    width: auto;
  }
`;

export const NotesInput = styled(Textarea)`
  border: 1.5px solid ${colors.main.black};
  border-radius: 12px;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.5;
  padding: 14px;
  resize: none;
  width: 100%;

  &::placeholder {
    color: ${colors.main.placeholdersAndDisabledBtnsText};
    opacity: 1;
  }

  &:focus {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }
`;
